import React, { useEffect, useRef, useState } from 'react'
import { t } from 'ttag'
import { Box, Checkbox, FormControlLabel, Typography, useMediaQuery, useTheme } from '@material-ui/core'
import YumButton from '../../components/YumButton/YumButton'
import YumSubmitButton from '../../components/YumSubmitButton/YumSubmitButton'
import { Card } from '../../components/Card/Card'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import YumSmallSelect from '../../components/YumSmallSelect/YumSmallSelect'
import YumHeader from '../../components/YumHeader/YumHeader'
import YumDialog from '../../components/YumDialog/YumDialog'
import YumDialogTitle from '../../components/YumDialogTitle/YumDialogTitle'
import YumDialogContent from '../../components/YumDialogContent/YumDialogContent'
import { UserCompanyDto } from '../../services/api.types'
import { ImportDataResult } from './store/ImportDataAdminReducer'
import { ReactComponent as UploadIcon } from '../../assets/v3/icons/announcements/upload.svg'

export interface IImportDataAdminProps {
  managedCompanies: UserCompanyDto[]
  uploading: boolean
  result: ImportDataResult | null
  selectedCompanyId: string | null
}

export interface IImportDataAdminDispatchProps {
  importData: (payload: { file: File; companyId: string; allowLowerValues: boolean; lockData: boolean }) => void
  clearImportDataResult: () => void
  setSelectedCompanyId: (payload: string) => void
}

type Props = IImportDataAdminProps & IImportDataAdminDispatchProps

export default function ImportDataAdmin(props: Props) {
  const {
    managedCompanies,
    uploading,
    result,
    selectedCompanyId,
    importData,
    clearImportDataResult,
    setSelectedCompanyId,
  } = props

  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const [file, setFile] = useState<File | null>(null)
  const [allowLowerValues, setAllowLowerValues] = useState(false)
  const [lockData, setLockData] = useState(false)
  const [activityTypesOpen, setActivityTypesOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const companyId = selectedCompanyId || (managedCompanies.length > 0 ? managedCompanies[0].companyId : null)

  useEffect(() => {
    if (companyId && !selectedCompanyId) {
      setSelectedCompanyId(companyId)
    }
  }, [])

  useEffect(() => {
    setFile(null)
    clearImportDataResult()
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [companyId])

  // Clear file input after upload completes
  useEffect(() => {
    if (!uploading && result) {
      setFile(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }, [uploading, result])

  const handleCompanyChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedCompanyId(event.target.value as string)
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && files.length > 0) {
      setFile(files[0])
      clearImportDataResult()
    }
  }

  const handleSubmit = () => {
    if (!file || !companyId || uploading) return
    importData({ file, companyId, allowLowerValues, lockData })
  }

  return (
    <Card>
      <NotificationsWrapper />

      <YumHeader>
        <h1>{t`Import data`}</h1>
        {managedCompanies.length > 1 && (
          <YumSmallSelect variant="outlined" value={companyId} onChange={handleCompanyChange}>
            {managedCompanies.map((uc: UserCompanyDto, i: number) => (
              <option key={i} value={uc.companyId}>
                {uc.companyName}
              </option>
            ))}
          </YumSmallSelect>
        )}
      </YumHeader>

      <Typography variant="body2" style={{ color: '#666', marginBottom: 16 }}>
        {t`Upload an Excel file (.xlsx) to import activity data for users. Each row represents one data entry for a user on a specific date. Maximum 1000 rows per import.`}
      </Typography>

      <Typography variant="body2" style={{ marginBottom: 4 }}>
        <strong>{t`Columns:`}</strong>
      </Typography>
      <ul style={{ margin: '0 0 8px 0', paddingLeft: 24 }}>
        <li>email</li>
        <li>date (YYYY-MM-DD)</li>
        <li>
          activity_type (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault()
              setActivityTypesOpen(true)
            }}
            style={{ color: '#1976d2' }}
          >
            {t`see available types`}
          </a>
          )
        </li>
        <li>manual_value</li>
        <li>integration_value</li>
      </ul>

      <Typography variant="body2" style={{ marginBottom: 24 }}>
        <a href="/samples/import-data-sample.xlsx" download style={{ color: '#1976d2' }}>
          {t`Download sample file`}
        </a>
      </Typography>

      <Box style={{ marginBottom: 16 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={handleFileChange}
          disabled={uploading}
          id="import-data-file"
          style={{ display: 'none' }}
        />
        <label htmlFor="import-data-file" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <YumButton variant="contained" color="primary" component="span" size="small" disabled={uploading}>
            <UploadIcon style={{ marginTop: '-2px', marginRight: '8px' }} />
            {t`Choose file`}
          </YumButton>
          {file && (
            <Typography variant="body2" style={{ color: '#666' }}>
              {file.name}
            </Typography>
          )}
        </label>
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={allowLowerValues}
            onChange={(e) => setAllowLowerValues(e.target.checked)}
            color="primary"
            disabled={uploading}
          />
        }
        label={t`Allow changing to lower values`}
        style={{ marginBottom: -4, display: 'block' }}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={lockData}
            onChange={(e) => setLockData(e.target.checked)}
            color="primary"
            disabled={uploading}
          />
        }
        label={t`Lock the specified data (user will not be able to change it)`}
        style={{ marginBottom: 16, display: 'block' }}
      />

      <Box>
        <YumSubmitButton
          text={t`Import`}
          color="primary"
          variant="contained"
          disabled={!file || uploading}
          submitInProgress={uploading}
          onClick={handleSubmit}
        />
      </Box>

      {result && (
        <Box
          style={{
            marginTop: 24,
            padding: 16,
            backgroundColor: '#f5f5f5',
            borderRadius: 8,
          }}
        >
          <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 8 }}>
            {t`Import results`}
          </Typography>
          <Typography variant="body2">
            {t`Total rows`}: {result.totalRows}
          </Typography>
          <Typography variant="body2">
            {t`Imported`}: {result.imported}
          </Typography>
          <Typography variant="body2">
            {t`Skipped`}: {result.skipped}
          </Typography>
          {result.errors && result.errors.length > 0 && (
            <Box style={{ marginTop: 12 }}>
              <Typography variant="body2" style={{ fontWeight: 600, color: '#d32f2f', marginBottom: 4 }}>
                {t`Errors`} ({result.errors.length}):
              </Typography>
              <Box style={{ maxHeight: 200, overflow: 'auto' }}>
                {result.errors.map((err, idx) => (
                  <Typography key={idx} variant="body2" style={{ color: '#d32f2f', fontSize: 13 }}>
                    {t`Row`} {err.row}: {err.message}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      <YumDialog open={activityTypesOpen} maxWidth="md" fullWidth>
        <YumDialogTitle handleClose={() => setActivityTypesOpen(false)}>{t`Available activity types`}</YumDialogTitle>
        <YumDialogContent style={{ width: 'auto' }}>
          <Box style={{ display: 'grid', gridTemplateColumns: isDesktop ? 'repeat(4, 1fr)' : '1fr', gap: '2px 24px' }}>
            {[
              'STEPS',
              'CALORIES',
              'INTENSIVE_MINUTES',
              'EXERCISE_MIN',
              'EXERCISE_HR',
              'RUNNING_KM',
              'RUNNING_MI',
              'RUNNING_MIN',
              'RUNNING_HR',
              'WALKING_KM',
              'WALKING_MI',
              'WALKING_MIN',
              'WALKING_HR',
              'CYCLING_KM',
              'CYCLING_MI',
              'CYCLING_HR',
              'CYCLING_MIN',
              'DISTANCE_KM',
              'DISTANCE_MI',
              'SWIMMING_M',
              'SWIMMING_YD',
              'SWIMMING_KM',
              'SWIMMING_MI',
              'SWIMMING_MIN',
              'SWIMMING_HR',
              'SLEEP_HR',
              'SLEEP_MIN',
              'MINDFULNESS_MIN',
              'MINDFULNESS_HR',
              'YOGA_MIN',
              'YOGA_HR',
              'STRENGTH_TRAINING_MIN',
              'STRENGTH_TRAINING_HR',
              'OUTDOOR_TIME_MIN',
              'OUTDOOR_TIME_HR',
              'PLANKING_MIN',
              'PLANKING_HR',
              'WATER_DRINKING_GLASS',
              'WATER_DRINKING_ML',
              'WATER_DRINKING_CUP',
              'WATER_DRINKING_OZ',
              'BOOK_READING_MIN',
              'BOOK_READING_BOOKS',
              'BOOK_READING_PAGES',
              'APPLE_EXERCISE_MIN',
              'GOOGLE_FIT_MOVE_MINUTES',
              'OTHER_MIN',
              'ENCOURAGING_PEOPLE',
              'CAR_FREE_DAYS',
              'ALCOHOL_ABSTINENCE',
              'SQUATTING',
              'CONTRAST_SHOWER_SEC',
              'FINANCE_TRACKING',
              'HEALTHY_COOKING_MEALS',
              'DEEP_BREATHING_MIN',
              'DAILY_JOURNALING',
              'PUSH_UPS',
              'PULL_UPS',
              'CREATIVE_ARTS_MIN',
              'SOCIALIZING_WITH_FRIENDS_MIN',
              'COLD_PLUNGE_SEC',
              'INTERMITTENT_FASTING_HR',
              'GRATITUDE_JOURNALING',
              'LANGUAGE_LEARNING_MIN',
              'HOBBY_DEVELOPMENT_MIN',
              'NATURAL_LIGHT_EXPOSURE',
              'SMOKING_CESSATION',
              'PHONE_FREE_TIME_HR',
              'CAFFEINE_REDUCTION',
              'SCREEN_FREE_MEALS',
              'DAILY_FLOSSING',
              'STRESS_LEVEL_MONITORING',
              'MOOD_TRACKING',
            ].map((type) => (
              <Typography
                key={type}
                variant="body2"
                style={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.8, whiteSpace: 'nowrap' }}
              >
                {type}
              </Typography>
            ))}
          </Box>
        </YumDialogContent>
      </YumDialog>
    </Card>
  )
}
